import type BABYLON from 'babylonjs';

import type { Color } from './color';
import { toColor4Option } from './color';
import { createEnvironmentTexture } from './environment';
import type { Globals } from './globals';
import { compactOptions } from './options';

// cf. https://doc.babylonjs.com/typedoc/classes/babylon.scene

export function addOnceObserverOnNewMeshAdded(scene: BABYLON.Scene, callback: () => void): void {
  const currentMeshesSize = scene.meshes.length;
  scene.whenReadyAsync().then(() => {
    if (scene.meshes.length !== currentMeshesSize) {
      callback();
    } else {
      scene.onNewMeshAddedObservable.addOnce(callback);
    }
  });
}

function addOnceObserverOnAfterRender(scene: BABYLON.Scene, callback: () => void): void {
  if (scene.getEngine().frameId) {
    callback();
  } else {
    scene.onAfterRenderObservable.addOnce(callback);
  }
}

export interface SceneOptions extends Pick<Partial<BABYLON.Scene>, 'skipFrustumClipping'> {
  clearColor?: Color | null;
  environmentTexture?: Parameters<typeof createEnvironmentTexture>[2] | null;
  debugLayerVisiblity?: boolean;
}

export function createOrUpdateScene(
  globals: Globals,
  engine: BABYLON.Engine,
  { clearColor, environmentTexture, debugLayerVisiblity, ...options }: SceneOptions = {}
): BABYLON.Scene {
  const scene = engine.scenes[0] || new globals.BABYLON.Scene(engine);
  Object.assign(
    scene,
    compactOptions({
      clearColor: toColor4Option(globals, clearColor),
      environmentTexture:
        environmentTexture != null
          ? createEnvironmentTexture(globals, scene, environmentTexture)
          : environmentTexture,
      ...options,
    })
  );
  if (debugLayerVisiblity != null) {
    addOnceObserverOnAfterRender(scene, () => {
      if (debugLayerVisiblity) {
        scene.debugLayer.show({ embedMode: true });
      } else {
        scene.debugLayer.hide();
      }
    });
  }
  return scene;
}
